import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { BlogPost } from '../types'

interface UseBlogPostsOptions {
  page?: number
  limit?: number
  search?: string
  category?: string
  isPublished?: boolean
}

export function useBlogPosts(client: SupabaseClient | null, options: UseBlogPostsOptions = {}) {
  const { page = 1, limit = 20, search = '', category, isPublished } = options

  const [posts, setPosts] = useState<BlogPost[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPosts = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('blog_posts')
        .select('*', { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%`)
      }

      // Apply category filter
      if (category) {
        query = query.eq('category', category)
      }

      // Apply published filter
      if (isPublished !== undefined) {
        query = query.eq('is_published', isPublished)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('published_at', { ascending: false, nullsFirst: false })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setPosts(data || [])
      setTotal(count || 0)
    } catch (err: any) {
      console.error('Error fetching blog posts:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, search, category, isPublished])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  return { posts, total, isLoading, error, refetch: fetchPosts }
}

export function useBlogPostMutations(client: SupabaseClient | null) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createPost = useCallback(async (post: Partial<BlogPost>) => {
    if (!client) {
      console.error('No Supabase client available')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log('Creating blog post with data:', post)
      const { data, error: insertError } = await client
        .from('blog_posts')
        .insert(post)
        .select()
        .single()

      if (insertError) {
        console.error('Supabase insert error:', insertError)
        throw insertError
      }
      console.log('Blog post created successfully:', data)
      return data
    } catch (err: any) {
      console.error('Error creating blog post:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const updatePost = useCallback(async (id: string, updates: Partial<BlogPost>) => {
    if (!client) return null

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await client
        .from('blog_posts')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (updateError) throw updateError
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const deletePost = useCallback(async (id: string) => {
    if (!client) return false

    setIsLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await client
        .from('blog_posts')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [client])

  return { createPost, updatePost, deletePost, isLoading, error }
}
